import { useEffect, useMemo } from 'react';
import type { DictaphoneProduct } from './products/types';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import usePreviousDistinct from 'react-use/esm/usePreviousDistinct';
import { createDictaphoneMatchSignature } from './matchSignature';

type UseDictaphoneProductMetricsParams = {
  audioInputDeviceLabel: string;
  isMicrophoneLoaded: boolean;
  matchingProduct: DictaphoneProduct | undefined;
  matchingProducts: ReadonlySet<DictaphoneProduct>;
};

const trackedMatchSignatures = new Set<string>();

export const __resetTrackedMatchSignaturesForTests = () => {
  trackedMatchSignatures.clear();
};

export const useDictaphoneProductMetrics = ({
  audioInputDeviceLabel,
  isMicrophoneLoaded,
  matchingProduct,
  matchingProducts,
}: UseDictaphoneProductMetricsParams): void => {
  const matchingProductIds = useMemo(
    () => Array.from(matchingProducts).map((product) => product.id),
    [matchingProducts]
  );
  const selectedProductId = matchingProduct?.id;
  const matchSignature = useMemo(
    () =>
      createDictaphoneMatchSignature({
        deviceLabel: audioInputDeviceLabel,
        matchingProductIds,
        selectedProductId,
      }),
    [audioInputDeviceLabel, matchingProductIds, selectedProductId]
  );
  const previousMatchSignature = usePreviousDistinct(matchSignature);

  useEffect(() => {
    if (!isMicrophoneLoaded && audioInputDeviceLabel === '') return;
    if (previousMatchSignature === matchSignature) return;
    if (trackedMatchSignatures.has(matchSignature)) return;
    trackedMatchSignatures.add(matchSignature);

    analytics.track(reporter.sys.dictaphoneProductMatchEvaluated, {
      deviceLabel: audioInputDeviceLabel,
      matchCount: matchingProducts.size,
      matchingProductIds,
      selectedProductId,
    });
  }, [
    audioInputDeviceLabel,
    isMicrophoneLoaded,
    matchSignature,
    matchingProductIds,
    matchingProducts,
    previousMatchSignature,
    selectedProductId,
  ]);
};
